/**
 * Pure die-cube geometry for the CSS-3D combat dice (ADR-0017 §1). No DOM, no
 * state — just the lookup tables the overlay reads to build and tumble a die:
 *
 *   - `faceRestPose(face)`  → the cube transform that brings `face` to the camera
 *   - `facePips(face)`      → the lit cells of a 3×3 pip grid for `face`
 *   - `tumbleStartPose(face, index)` → the spun-up start pose a die transitions
 *     *from* on roll-phase entry; derived from face + index so spin varies
 *     without `Math.random` (the engine is the only sanctioned RNG, ADR-0015 §3).
 *
 * Opposite faces sum to 7 (western die convention) so the cube reads as a real
 * die from any angle. The landing pose is always the bare rest pose, so the
 * rolled face ends square to the camera; the start pose carries the extra turns
 * and a small offset that the CSS transition unwinds on the way in.
 */

import type { DieFace } from '../types';

/** Face → cube rest transform (bring face N toward the camera). ADR-0017 §1. */
const REST_POSE = {
  1: 'rotateX(0deg) rotateY(0deg)',
  2: 'rotateY(-90deg)',
  3: 'rotateX(-90deg)',
  4: 'rotateX(90deg)',
  5: 'rotateY(90deg)',
  6: 'rotateY(180deg)',
} as const satisfies Record<DieFace, string>;

/** Lit pip cells per face value, 1-indexed in a row-major 3×3 grid:
 *  1 2 3
 *  4 5 6
 *  7 8 9
 * Centre for odd faces; corners/edges fill the rest (opposite faces sum to 7). */
const PIPS = {
  1: [5],
  2: [1, 9],
  3: [1, 5, 9],
  4: [1, 3, 7, 9],
  5: [1, 3, 5, 7, 9],
  6: [1, 3, 4, 6, 7, 9],
} as const satisfies Record<DieFace, readonly number[]>;

/** The cube transform that lands `face` square to the camera. Pure 6-entry
 * lookup — the tumble transitions *to* this pose. */
export function faceRestPose(face: DieFace): string {
  return REST_POSE[face];
}

/** The cells (1..9) of a 3×3 grid that carry a pip for `face`. */
export function facePips(face: DieFace): readonly number[] {
  return PIPS[face];
}

/** Deterministic spin parameters for a die's tumble, keyed off `face` + pool
 * `index` so each die spins a little differently with no RNG. `turns*` are whole
 * extra revolutions; `offset*` are small (deg) perturbations unwound by the time
 * the cube settles on the rest pose. */
export function tumbleSpin(
  face: DieFace,
  index: number,
): { turnsX: number; turnsY: number; offsetX: number; offsetY: number } {
  const i = Math.max(0, Math.trunc(index));
  return {
    turnsX: 2 + ((i + face) % 2), // 2..3 whole turns
    turnsY: 3 + ((i * 2 + face) % 3), // 3..5 whole turns
    offsetX: ((i * 7 + face * 5) % 9) - 4, // -4..4 deg
    offsetY: ((i * 11 + face * 3) % 9) - 4, // -4..4 deg
  };
}

/** The spun-up transform a die starts from before tumbling in: the rest pose
 * plus whole extra turns and a small offset. A CSS transition from here to the
 * bare `faceRestPose(face)` takes "the long way round" and lands on the face. */
export function tumbleStartPose(face: DieFace, index: number): string {
  const { turnsX, turnsY, offsetX, offsetY } = tumbleSpin(face, index);
  const x = 360 * turnsX + offsetX;
  const y = 360 * turnsY + offsetY;
  return `${faceRestPose(face)} rotateX(${x}deg) rotateY(${y}deg)`;
}
